/**
 * API Client
 *
 * Fetch wrapper that automatically handles:
 * - Base URL configuration
 * - Authentication headers (Bearer token)
 * - Error handling
 * - JSON parsing
 */

const MOCK_AUTH_STORAGE_KEY = 'brickmod-mock-auth';

/**
 * Get the API base URL from environment or default to localhost
 */
function getBaseUrl(): string {
  // In browser environment, use Vite env var
  if (typeof window !== 'undefined' && import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Default to local development
  return 'http://localhost:8787';
}

/**
 * Generate a mock auth token from the stored user
 * Format: base64(userId:timestamp)
 */
function getMockAuthToken(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(MOCK_AUTH_STORAGE_KEY);
    if (!stored) return null;

    const user = JSON.parse(stored);
    if (!user?.id) return null;

    // Create token in format expected by backend: base64(userId:timestamp)
    const tokenData = `${user.id}:${Date.now()}`;
    return btoa(tokenData);
  } catch {
    return null;
  }
}

/**
 * API Error class for handling HTTP errors
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status: number,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Request options for API calls
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  requiresAuth?: boolean;
}

/**
 * Make an API request
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, requiresAuth = false, ...init } = options;

  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };

  // Add auth header if required or if we have a token
  const token = getMockAuthToken();
  if (token && (requiresAuth || token)) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Check if auth is required but no token available
  if (requiresAuth && !token) {
    throw new ApiError('Authentication required', 'UNAUTHORIZED', 401);
  }

  const response = await fetch(url, {
    ...init,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Handle non-JSON responses
  const contentType = response.headers.get('content-type');
  if (!contentType?.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError(
        `Request failed: ${response.statusText}`,
        'NETWORK_ERROR',
        response.status
      );
    }
    return {} as T;
  }

  const data = await response.json();

  if (!response.ok) {
    throw new ApiError(
      data.error || 'Request failed',
      data.code || 'UNKNOWN_ERROR',
      response.status,
      data.details
    );
  }

  return data as T;
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'POST', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PATCH', body }),

  put: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'PUT', body }),

  delete: <T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};
